import {
  Accordion,
  styled,
  AccordionSummary,
  AccordionDetails,
  ListItemSecondaryAction,
  ListItemText,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Text, Icons, Grid, List, Button } from '@/theme/index';
import { scrollToElement } from '@/utils/scrollToElement';
import { useScrollPosition } from '@/utils/useScrollPosition';
import { NavLinkData } from '../DesktopNav/DesktopNav';
import { NavLinkItem } from '../NavLinkItem/NavLinkItem';

interface ReportNavMobileProps {
  navLinkData: NavLinkData[];
  navLinkClickEventCategory?: string;
}

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: 'none',
  display: 'none',
  border: `1px solid ${theme.palette.grey[300]}`,
  position: 'fixed',
  bottom: 0,
  width: '100%',
  maxWidth: '100vw',
  zIndex: 15,
  [theme.breakpoints.down('md')]: {
    display: 'block',
  },
}));

const StyledList = styled(List)(({ theme }) => {
  const { spacing } = theme;
  return `
    display: flex;
    flex-direction: column;
    gap: ${spacing(1)};
  `;
});

export const ReportNavMobile = ({
  navLinkData,
  navLinkClickEventCategory,
}: ReportNavMobileProps) => {
  const [active, setActive] = useState<string>(navLinkData[0].href);
  const [sectionName, setSectionName] = useState<string | undefined>(
    navLinkData[0].text
  );
  const [currentPosition, setCurrentPosition] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [transformStyle, setTransformStyle] = useState({
    transform: 'translateY(0)',
    transition: 'transform 0.3s ease-in-out',
  });
  const scrollPosititon = useScrollPosition();

  const isFooterInView = () => {
    const footerRect = document
      .getElementById('footer')
      ?.getBoundingClientRect();
    if (footerRect?.top) return footerRect.top < window.innerHeight;
  };

  useEffect(() => {
    const handleSetActive = () => {
      const reportViewTop = 0;
      const reportViewBottom = window.innerHeight;
      const reportViewMiddle = (reportViewBottom - reportViewTop) / 2;
      const hrefs = navLinkData.map((item) => item.href);
      const positions = hrefs.map((href: string) => {
        const id = href.replace('#', '');
        const element = document.getElementById(id);
        if (!element) {
          return null;
        }

        const bounding = element.getBoundingClientRect();
        return {
          href,
          top: bounding.top,
          bottom: bounding.bottom,
        };
      });

      const sectionsInTopHalfOfView = positions.filter((pos) => {
        if (pos) return pos.top >= reportViewTop && pos.top < reportViewMiddle;
      });
      const sectionsAboveTopOfView = positions.filter((pos) => {
        if (pos) return pos.top < reportViewTop;
      });
      const sectionsBelowMiddleOfView = positions.filter((pos) => {
        if (pos) return pos.top >= reportViewMiddle;
      });
      const firstSectionInView = sectionsInTopHalfOfView[0];
      const lastSectionAboveView = sectionsAboveTopOfView.reverse()[0];
      const firstSectionBelowViewMiddle = sectionsBelowMiddleOfView[0];
      const prioritizedSections = [
        firstSectionInView,
        lastSectionAboveView,
        firstSectionBelowViewMiddle,
      ];
      const activeSection = prioritizedSections.find((section) => section);
      if (activeSection) {
        setActive(activeSection.href);
        const section = navLinkData.find((element) => {
          if (activeSection.href === element.href) return element.text;
        });
        setSectionName(section?.text);
        const position = navLinkData.findIndex(
          (element) => element.text === section?.text
        );
        setCurrentPosition(position);
      }
    };
    const hideAccordianWhenFooterInView = () => {
      if (isFooterInView()) {
        setTransformStyle({ ...transformStyle, transform: 'translateY(100%)' });
      } else {
        setTransformStyle({ ...transformStyle, transform: 'translateY(0)' });
      }
    };
    return () => {
      handleSetActive();
      hideAccordianWhenFooterInView();
    };
  }, [scrollPosititon]);

  const handleClose = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    setExpanded(false);
  };

  const handleNextSection = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    if (currentPosition === navLinkData.length - 1) return;
    scrollToElement(navLinkData[currentPosition + 1].href.replace('#', ''));
  };

  const handlePreviousSection = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    if (currentPosition === 0) return;
    scrollToElement(navLinkData[currentPosition - 1].href.replace('#', ''));
  };

  const AccordionSummaryStyles = {
    margin: 0,
    '.MuiAccordionSummary-content.Mui-expanded': {
      marginBottom: 0,
    },
  };

  return (
    <StyledAccordion expanded={expanded} sx={transformStyle}>
      <AccordionSummary
        aria-controls="panel1a-content"
        id="panel1a-header"
        onClick={() => setExpanded(true)}
        sx={AccordionSummaryStyles}
      >
        <Grid container>
          {expanded ? (
            <Button
              fullWidth
              onClick={handleClose}
              disableRipple
              sx={{ display: 'block' }}
              color="success"
            >
              <Text margin={0}>
                <Icons.CloseRounded fontSize="small" />
              </Text>
              <Text variant="h5" marginBottom={0}>
                Close Menu
              </Text>
            </Button>
          ) : (
            <>
              <Grid item container alignItems="center" xs={8}>
                <Icons.FormatListBulleted color="primary" fontSize="large" />{' '}
                <Text color="primary" variant="h6" margin={0}>
                  {sectionName}
                </Text>
              </Grid>
              <Grid item sx={{ marginLeft: 'auto' }}>
                <Icons.ExpandLess
                  color="primary"
                  fontSize="large"
                  onClick={handlePreviousSection}
                />{' '}
                <Icons.ExpandMore
                  color="primary"
                  fontSize="large"
                  onClick={handleNextSection}
                />
              </Grid>
            </>
          )}
        </Grid>
      </AccordionSummary>
      <AccordionDetails sx={{ maxHeight: '75vh', overflow: 'scroll' }}>
        <StyledList>
          {navLinkData.map((item, index) => (
            <NavLinkItem
              key={item.href}
              active={active}
              navLink={item}
              index={index}
              navLinkClickEventCategory={navLinkClickEventCategory}
              setExpanded={setExpanded}
            >
              <ListItemText>{item.text}</ListItemText>
              {!item.hideReportItemsCount && (
                <ListItemSecondaryAction
                  sx={{
                    fontWeight: 'bold',
                    alignItems: 'center',
                    display: 'flex',
                  }}
                >
                  {item.icon && item.showIcon ? (
                    <>{item.icon}</>
                  ) : (
                    <Text sx={{ fontWeight: 'inherit' }}>
                      {item.reportItemsCount || 0}
                    </Text>
                  )}
                </ListItemSecondaryAction>
              )}
            </NavLinkItem>
          ))}
        </StyledList>
      </AccordionDetails>
    </StyledAccordion>
  );
};
